import React, {useContext, useEffect, useState} from "react";
import { useParams, useNavigate } from "react-router-dom";

import { Wrapper as GoogleMapsWrapper, Status as GoogleMapsStatus } from "@googlemaps/react-wrapper";
import GoogleMaps from "/src/components/GoogleMaps"
import List from "/src/components/List"
import {SettingsContext} from "/src/SettingsContext";
import {LanguageContext} from "/src/LanguageContext";
import {PointContext} from "/src/PointContext";
import axiosInstance from "../../axiosInstance";
import {VisitedContext} from "/src/VisitedContext";
import Map from "../components/Map";
import {ListBulletIcon, MapIcon} from "@heroicons/react/24/outline";

import {useTranslation} from "react-i18next";
import {useModal} from "/src/ModalContext";
import QRCodeScanner from "@/components/QRCodeScanner";
import {QrCodeIcon} from "@heroicons/react/24/solid";
import {ToursContext} from "@/ToursContext";
import TourPoint from "@/pages/TourPoint";
import {useOverlay} from "@/OverlayContext";
import { usePointPreview } from '/src/PointPreviewContext';
import PointPreview from "@/components/PointPreview";
import MapLibreMap from "@/components/MapLibreMap";
import { useLocation } from '/src/LocationContext';

export default function Points({selectedView}) {
    const params = useParams();
    const navigate = useNavigate();
    const pointParam = params.point;

    const { tourSettings } = useContext(SettingsContext);
    const languageContext = useContext(LanguageContext);
    const visitedContext = useContext(VisitedContext);
    const pointContext = useContext(PointContext);
    const { activeTour } = useContext(ToursContext);

    const pointNavigation = pointContext.pointNavigation;

    const selectedLanguage = languageContext.state.selected;
    const unlockedPoints = pointContext.unlocked;
    const visitedPoints = visitedContext.points;

    const [fetchedPoints, setFetchedPoints] = useState({})
    const [activePoint, setActivePoint] = useState(null)
    const { 
        currentLocation, 
        startWatchingLocation, 
        stopWatchingLocation,
        getGPSPosition 
    } = useLocation();

    const [display, setDisplay] = useState("")

    const gpsRequired = (tourSettings.navigation?.google_maps?.active || tourSettings.navigation?.open_streetmap?.active);

    const { setOverlay, isOverlayOpen, setOverlayIsVisible } = useOverlay();
    //const [navigation, setNavigation] = useState(null);

    const { isModalOpen, openModal, closeModal, modalContent, updateModalContent } = useModal();

    const { isPreviewOpen, openPreview } = usePointPreview();

    // Count the number of objects where the 'active' property is true
    const activeNavigation = selectedView ? [] : Object.keys(tourSettings.navigation).filter(key => tourSettings.navigation[key].active);

    const { t } = useTranslation();
    const getMapToggleName = () => {
        if (tourSettings.navigation?.google_maps?.active) {
            return 'google_maps';
        }
        if (tourSettings.navigation?.open_streetmap?.active) {
            return 'open_streetmap';
        }
        if (tourSettings.navigation?.map?.active) {
            return 'map'
        }
        if (tourSettings.navigation?.list?.active) {
            return 'list'
        }
        if (tourSettings.navigation?.qr_code?.active) {
            return 'qr_code'
        }
        return null;
    }

    React.useEffect(() => {
        console.log(`pointNavigation changed to ${pointNavigation}`);
        setDisplay(pointNavigation);
    }, [pointNavigation])


    const switchPointNavigation = (navigation_name) => {
        pointContext.switchPointNavigation(navigation_name);
        setDisplay(navigation_name);
    }

    useEffect(() => {
        loadPoints()

        console.log("available navigation options: ", tourSettings.navigation);
        console.log("active pointNavigation", pointNavigation);

        setDisplay((selectedView) ? selectedView : pointNavigation);

        // get current GPS position from device
        if (gpsRequired) {
            console.log("Get current GPS position. Google maps or open street map is enabled.");
            startWatchingLocation();
        } else {
            console.log("GPS is not required Google maps or open street map is not enabled!");
        }

        // Clean up when component unmounts
        return () => {
            if (gpsRequired) {
                stopWatchingLocation();
            }
        };
    }, [activeTour, tourSettings])

    useEffect(() => {
        if (Object.keys(fetchedPoints).length > 0) {
            updatePointStates();
        } else {
            console.log("do not call updatePointStates() since the fetchedPoints are not loaded yet");
        }
    }, [fetchedPoints, currentLocation, tourSettings])


    // Function to handle item click
    const handleItemClick = (pointClicked, showPreview = true) => {
        if (pointClicked && pointClicked?.point?.id) {
            console.log("handleItemClick for point", pointClicked);

            // Open the preview based on the ListItemTemplate
            if (showPreview) {
            openPreview(pointClicked, (point) => {
                // This callback will run when the user clicks on the preview or the "View Details" button
                setActivePoint(point);
                
                // Update URL without triggering navigation
                const pointUrl = `/${params.language ? `${params.language}/` : ''}point/${point.point.id}`;
                window.history.replaceState(null, '', pointUrl);
            });
            } else {
                // Set the active point directly
                setActivePoint(pointClicked);
                    
                // Update URL without triggering navigation
                const pointUrl = `/${params.language ? `${params.language}/` : ''}point/${pointClicked.point.id}`;
                window.history.replaceState(null, '', pointUrl);
            }
        }
    }

    // Effect to open overlay when activePoint is set
    useEffect(() => {
        console.log("overlay open based on route ", isOverlayOpen, activePoint);
        // open point (based on route)
        if (activePoint && !isOverlayOpen) {
            console.log("open overlay for point", activePoint);

            setOverlay(
                <TourPoint 
                    tourPoint={activePoint} 
                    onContentLoaded={() => {
                        console.log('Content loaded callback triggered');
                        setOverlayIsVisible(true);
                    }} 
                />,
                null, // Title
                null, // Subtitle
                null, // Image
                () => {
                    // This function will be called when the overlay is closed
                    console.log('Overlay closed callback triggered');

                    
                    // Update URL back to points page without navigating
                    const pointsUrl = `/${params.language ? `${params.language}/` : ''}points`;
                    window.history.replaceState(null, '', pointsUrl);
                    
                    // Don't reset activePoint here - this allows reopening if needed
                    setActivePoint(null);
                }
            );
        } else {
            console.log("overlay is not open, do not open overlay for point", activePoint);

        }
    }, [activePoint, isOverlayOpen]);

    // Effect to update activePoint when URL parameter changes
    useEffect(() => {
        if (pointParam && Object.keys(fetchedPoints).length > 0) {
            const pointData = fetchedPoints[pointParam]?.data;
            if (pointData) {
                console.log("Setting active point from URL parameter:", pointData);
                setActivePoint(pointData);
            }
        }
    }, [pointParam, fetchedPoints]);

    const render = (status) => {
        switch (status) {
            case GoogleMapsStatus.LOADING:
                return "loading..";
            case GoogleMapsStatus.FAILURE:
                return "error";
            case GoogleMapsStatus.SUCCESS:
                return <GoogleMaps currentLocation={currentLocation} points={fetchedPoints} onItemClick={handleItemClick}/>;
        }
    };

    if (tourSettings.introduction_page_id) {
  //      return "toon introductiepagina";
    }

    const loadPoints = async () => {
        console.log("loadPoints", activeTour);

        try {

            // Check if tourSettings is available before making the API call
            if (!activeTour || !tourSettings || Object.keys(tourSettings).length === 0) {
                return;
            }

            console.log("axiosInstance header ", activeTour);
            let headers = {
                ...(activeTour && {
                    "X-Tour-Id": activeTour
                }),
            }
            const response = await axiosInstance.get(process.env.CLIENT_API_URL + "/client/tour-points", {headers: headers}) // get tour points

            // Simulate a delay of 1000 milliseconds (1 second)
            //await new Promise(resolve => setTimeout(resolve, 1000));

            let pointData = {};
            response.data.forEach((data) => {
                let point = {}
                point.data = data;
                point.state = (data.settings?.gps_lock) ? 'locked' : 'default'; // initial state
                point.locked = !!data.settings?.gps_lock;
                pointData[data.point.id] = point;
            })
            console.log(pointData)

           // if (window.location.hostname.includes("dev.tourio.com") || window.location.hostname.includes("localhost") && Object.keys(pointData).length >= 3) {
                //console.log(pointData[Object.keys(pointData)[0]].data);
                // // first point (Seleno, kantoor hek)
                // pointData[Object.keys(pointData)[0]].data.gps_location = "52.872787, 6.521087";
                // pointData[Object.keys(pointData)[0]].data.gps_lock = true;
                // pointData[Object.keys(pointData)[0]].state = "locked";
                // pointData[Object.keys(pointData)[0]].locked = true;
                //
                // // second point (Seleno, Veldakker)
                // pointData[Object.keys(pointData)[1]].data.gps_location = "52.872356, 6.520980";
                // pointData[Object.keys(pointData)[1]].data.gps_lock = true;
                // pointData[Object.keys(pointData)[1]].state = "locked";
                // pointData[Object.keys(pointData)[1]].locked = true;
                //
                // // third point (Seleno, Nijland)
                // pointData[Object.keys(pointData)[2]].data.gps_location = "52.872481, 6.522099";
                // pointData[Object.keys(pointData)[2]].data.gps_lock = true;
                // pointData[Object.keys(pointData)[2]].state = "locked";
                // pointData[Object.keys(pointData)[2]].locked = true;

                // // fourth point (Stefan, tuin)
                // pointData[Object.keys(pointData)[3]].data.gps_location = "52.855230, 6.512873";
                // pointData[Object.keys(pointData)[3]].data.gps_lock = true;
                // pointData[Object.keys(pointData)[3]].state = "locked";
                // pointData[Object.keys(pointData)[3]].locked = true;
                //
                // // fifth point (Stefan, speeltuin)
                // pointData[Object.keys(pointData)[4]].data.gps_location = "52.855388, 6.512406";
                // pointData[Object.keys(pointData)[4]].data.gps_lock = true;
                // pointData[Object.keys(pointData)[4]].state = "locked";
                // pointData[Object.keys(pointData)[4]].locked = true;


          //  }

            setFetchedPoints(pointData);

        } catch (error) {
            // Handle error if needed
            console.error(error);
        }

    }

    const updatePointStates = () => {
        console.log("updatePointStates");

        console.log("gps enabled 2?", tourSettings.navigation?.google_maps, gpsRequired);

        Object.entries(fetchedPoints).map(([pointId, point]) => {

            console.log("fetchedPoints data", fetchedPoints[pointId].data);

            const gpsLockEnabled = (fetchedPoints[pointId].data?.settings?.gps_lock > 0 && gpsRequired);
            console.log("point " + pointId + " lock setting: ", gpsLockEnabled);

            let distance = 0;
            // Calculate distance whenever GPS is available and point has location
            if (gpsRequired && fetchedPoints[pointId].data.settings?.gps_location) {
                distance = calcCrow(currentLocation, getGPSPosition(fetchedPoints[pointId].data.settings?.gps_location));
                console.log("distance point " + pointId + " " + distance + " <= " + tourSettings.navigation.google_maps.gps_radius);

                // Store the distance on the point object for potential use elsewhere
                fetchedPoints[pointId].distance = distance;
                
                // Only apply locking logic if GPS lock is enabled for this point
                if (gpsLockEnabled) {
                    fetchedPoints[pointId].locked = true; // Start with locked state
                    
                    if (distance >= 0 && distance <= tourSettings.navigation.google_maps.gps_radius) {
                        // Within range - unlock the point
                        fetchedPoints[pointId].locked = false;
                        fetchedPoints[pointId].state = "default";
                        setUnlocked(pointId, {"id": pointId});
                    } else {
                        // Outside range - keep locked unless previously unlocked
                        if (!Object.keys(unlockedPoints).includes(pointId)) {
                            fetchedPoints[pointId].locked = true;
                            fetchedPoints[pointId].state = "locked";
                        }
                    }
                } else {
                    // GPS lock not enabled for this point
                    fetchedPoints[pointId].locked = false;
                    fetchedPoints[pointId].state = "default";
                    setUnlocked(pointId, {"id": pointId});
                }
            } else {
                // No GPS or location data available
                fetchedPoints[pointId].locked = false;
                fetchedPoints[pointId].state = "default";
                setUnlocked(pointId, {"id": pointId});
            }

            /*
            available states:
            - default (lock enabled and within range OR lock disabled)
            - visited
            - answer_correct (quiz module)
            - answer_wrong (quiz module)

            all states can have a locked situation if gps lock is enabled for that point and outside range
             */

            // check if the point is within the range of the current position
            // if ( distance > 0 && fetchedPoints[pointId].locked) {
            //   fetchedPoints[pointId].locked = (gpsLockEnabled && distance > 0 && distance > tourSettings.gps_radius);
            // }



            //  fetchedPoints[pointId].locked = false; // for debug only



            if (Object.keys(visitedPoints).includes(pointId)) {
                if (gpsLockEnabled) {
                    fetchedPoints[pointId].locked = false; // remove lock when you visited the page once and lock is enabled
                }
                fetchedPoints[pointId].state = "visited"

                // check quiz module
                if (visitedPoints[pointId].hasOwnProperty("quiz_success")) {

                    fetchedPoints[pointId].state = (visitedPoints[pointId]["quiz_success"]) ? "answer_correct" : "answer_wrong";


                }

            }

            console.log("fetchedPoints before set",pointId, fetchedPoints[pointId]);

        })

        console.log("visitedPoints", visitedPoints);
        console.log("setFetchedPoints", fetchedPoints);

        // update state of the changed points
        setFetchedPoints(fetchedPoints)

    }


    // const getGPSPosition = (position) => {
    //     var LatLng = position.replace("(", "").replace(")", "").split(", ")
    //     var Lat = parseFloat(LatLng[0]);
    //     var Lng = parseFloat(LatLng[1]);

    //     return { lat: Lat, lng: Lng}
    // }

    const setUnlocked = (pointId, data) => {

        let unlockedPoints = {...pointContext.unlocked};

        if (typeof unlockedPoints[pointId] === "undefined" ) {

            // set new unlocked point data
            unlockedPoints[pointId] = { id: parseInt(pointId), ...data }

        } else {
            // add data to existing unlocked point data
            unlockedPoints[pointId] = { ...unlockedPoints[pointId], ...data }
        }
        pointContext.setUnlockedPoints(unlockedPoints);
    }


    //This function takes in latitude and longitude of two location and returns the distance between them as the crow flies (in km)
    function calcCrow(location1, location2)
    {
        let lat1 = location1.lat;
        let lon1 = location1.lng;
        let lat2 = location2.lat;
        let lon2 = location2.lng;

        // console.log("location1:",location1);
        // console.log("location2:",location2);
        // console.log("lat1:"+lat1);
        // console.log("lat2:"+lat2);
        // console.log("lon1:"+lon1);
        // console.log("lon2:"+lon2);


        let R = 6371; // km
        let dLat = toRad(lat2-lat1);
        let dLon = toRad(lon2-lon1);
        lat1 = toRad(lat1);
        lat2 = toRad(lat2);

        let a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.sin(dLon/2) * Math.sin(dLon/2) * Math.cos(lat1) * Math.cos(lat2);
        let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));



        let d =  R * c * 1000;
        return (isNaN(d)) ? null : d;

    }

    // Converts numeric degrees to radians
    function toRad(Value)
    {
        return Value * Math.PI / 180;
    }




    return (

        <>
            <div className={ `flex-1 relative ${(activeNavigation.length > 1 || activeNavigation.includes('qr_code')) && pointContext.showPointNavigation ? 'pb-24' : ''}`}>
                {(() => {
                    switch (display) {
                        case 'google_maps':
                            return <GoogleMapsWrapper apiKey={process.env.GOOGLE_MAPS_API_KEY} render={render}/>
                        case 'open_streetmap':
                            return <MapLibreMap currentLocation={currentLocation} points={fetchedPoints} onItemClick={handleItemClick}/>
                        case 'map':
                            return <Map currentLocation={currentLocation} points={fetchedPoints}  onItemClick={handleItemClick}/>
                        case 'list':
                            return <List currentLocation={currentLocation} points={fetchedPoints} onItemClick={handleItemClick}/>
                        case 'qr_code':
                            return <QRCodeScanner/>
                        default:
                            return null;
                    }
                })()}
            </div>

                {(pointNavigation.includes('google_maps') || pointNavigation.includes('open_streetmap') &&
                <div className="fixed bottom-8 left-0 right-0 z-10">
                    <PointPreview />
                </div>
                )}

            {(activeNavigation.length > 1 || activeNavigation.includes('qr_code')) && pointContext.showPointNavigation &&
                <div className={`fixed left-0 right-0 flex justify-center z-10 ${isPreviewOpen && (pointNavigation.includes('google_maps') || pointNavigation.includes('open_streetmap')) ? 'top-24' : 'bottom-8'}` }>

                    {activeNavigation.length > 1 &&
                        <div className={`relative py-2 w-full px-4`}>
                            <div
                                className="bg-white shadow-md rounded-full border-[3px] border-white mx-auto flex justify-between items-center">

                                {activeNavigation.includes('qr_code') && (window.location.hostname === "mas.tourio.com" || window.location.hostname === "mas.dev.tourio.com") &&
                                    <div
                                        className={`inline-flex items-center rounded-full py-2 px-5 max-sm:px-3 min-w-min w-1/2 justify-center`}
                                        style={{
                                            backgroundColor: `${(display === 'qr_code') ? tourSettings.contrast_color : ''}`,
                                            color: `${(display === 'qr_code') ? tourSettings.contrast_text_color : ''}`
                                        }}
                                        onClick={() => switchPointNavigation('qr_code')}>
                                        <QrCodeIcon className="h-5 w-5 cursor-pointer my-1"/>
                                        <span className="whitespace-nowrap ml-2 max-xs:hidden max-sm:text-xs">{t('qr_code')}</span>
                                    </div>
                                }

                                {(activeNavigation.includes('map') || activeNavigation.includes('google_maps') || activeNavigation.includes('open_streetmap')) &&
                                    <div
                                        className={`inline-flex items-center rounded-full py-2 px-5 max-sm:px-3  min-w-min w-1/2 justify-center`}
                                        style={{
                                            backgroundColor: `${(pointNavigation === 'google_maps' || pointNavigation === 'open_streetmap' || pointNavigation === 'map') ? tourSettings.contrast_color : ''}`,
                                            color: `${(pointNavigation === 'google_maps' || pointNavigation === 'open_streetmap' || pointNavigation === 'map') ? tourSettings.contrast_text_color : ''}`
                                        }} onClick={() => switchPointNavigation(getMapToggleName())}>
                                        <MapIcon className="h-5 w-5 cursor-pointer my-1"/>
                                        <span className="whitespace-nowrap ml-2 max-[280px]:hidden max-sm:text-xs">{t('map')}</span>
                                    </div>
                                }

                                {activeNavigation.includes('list') &&
                                    <div
                                        className={`inline-flex items-center rounded-full py-2 px-5 max-sm:px-3  min-w-min w-1/2 justify-center`}
                                        style={{
                                            backgroundColor: `${(pointNavigation === 'list') ? tourSettings.contrast_color : ''}`,
                                            color: `${(pointNavigation === 'list') ? tourSettings.contrast_text_color : ''}`
                                        }} onClick={() => switchPointNavigation('list')}>
                                        <ListBulletIcon className="h-5 w-5 cursor-pointer my-1"/>
                                        <span className="whitespace-nowrap ml-2 max-[280px]:hidden  max-sm:text-xs" >{t('list')}</span>
                                    </div>
                                }
                                {activeNavigation.includes('qr_code') && window.location.hostname !== "mas.tourio.com" && window.location.hostname !== "mas.dev.tourio.com" &&
                                    <div
                                        className={`inline-flex items-center rounded-full py-2 px-5 max-sm:px-3 min-w-min w-1/2 justify-center`}
                                        style={{
                                            backgroundColor: `${(display === 'qr_code') ? tourSettings.contrast_color : ''}`,
                                            color: `${(display === 'qr_code') ? tourSettings.contrast_text_color : ''}`
                                        }}
                                        onClick={() => switchPointNavigation('qr_code')}>
                                        <QrCodeIcon className="h-5 w-5 cursor-pointer my-1"/>
                                        <span className="whitespace-nowrap ml-2 max-xs:hidden max-sm:text-xs">{t('qr_code')}</span>
                                    </div>
                                }

                            </div>
                        </div>
                    }
                </div>
            }

        </>

    );
}
